/*
  Smooth open/close for the accordion rows (product details, FAQ).

  Horizon animates <details> with `interpolate-size`, which only Chromium supports, and
  skips the transition whenever the row isn't focused (Safari never focuses a tapped
  <summary>). On iPhone and in Safari the text therefore jumped open with a faint fade.
  This animates the height of the <details> itself with the Web Animations API, so it
  runs the same everywhere, and fades the text in once the row has started to open.
  Horizon's own CSS transition is switched off for these rows in ertelt-brand.css.
*/
(() => {
  const ROWS = '.accordion accordion-custom > details';
  const EASE = 'cubic-bezier(0.33, 0, 0.2, 1)';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Longer rows take a little longer, so the speed of the edge stays the same.
  const duration = (distance) => Math.min(650, Math.max(380, 320 + distance * 0.5));

  /** @type {WeakMap<HTMLDetailsElement, Animation>} */
  const running = new WeakMap();

  /** @param {HTMLDetailsElement} details */
  function toggle(details, open) {
    const summary = details.querySelector(':scope > summary');
    const content = details.querySelector(':scope > .details-content');
    if (!summary || !content) return false;

    const from = details.getBoundingClientRect().height;
    // Stop a row that is still moving, so a second tap reverses it from where it is.
    running.get(details)?.cancel();
    content.getAnimations().forEach((a) => a.cancel());

    // Measure the target height with the row in its end state.
    details.open = true;
    const closedHeight = summary.getBoundingClientRect().height + borders(details);
    const to = open ? details.getBoundingClientRect().height : closedHeight;

    details.style.overflow = 'clip';
    const animation = details.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: duration(Math.abs(to - from)), easing: EASE }
    );
    running.set(details, animation);

    content.animate(
      open
        ? { opacity: [0, 1], transform: ['translateY(-4px)', 'none'] }
        : { opacity: [1, 0] },
      open
        ? { duration: duration(to - from), delay: 60, easing: EASE, fill: 'backwards' }
        : { duration: 180, easing: 'ease-out', fill: 'forwards' }
    );

    animation.onfinish = () => {
      running.delete(details);
      details.style.overflow = '';
      if (!open) details.open = false;
      content.getAnimations().forEach((a) => a.cancel());
    };
    return true;
  }

  /** @param {Element} el */
  function borders(el) {
    const s = getComputedStyle(el);
    return parseFloat(s.borderTopWidth) + parseFloat(s.borderBottomWidth);
  }

  // Bubble phase, so Horizon's own summary handler (which cancels the click when a row
  // is disabled on this breakpoint) has already run.
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || reducedMotion.matches) return;
    const summary = event.target instanceof Element && event.target.closest('summary');
    const details = summary?.parentElement;
    if (!(details instanceof HTMLDetailsElement) || !details.matches(ROWS)) return;

    // `open` stays true until a closing row has finished, so mid-animation the
    // direction comes from data-ertelt-open. The icon CSS follows the same attribute.
    const open = running.has(details) ? details.dataset.erteltOpen !== 'true' : !details.open;
    details.dataset.erteltOpen = String(open);
    if (toggle(details, open)) event.preventDefault();
  });
})();
